import type { DefaultOptionType } from 'rc-select/lib/Select';
import React from 'react';

import DateTime from '~/components/Columns/DateTime';
import { prefetchOptions, toLabel } from '~/components/Columns/helpers';

import {
  type CollectionColumnDefinition,
  type ColumnDefinition,
  DateFilterPresets,
  ModelColumns,
} from '~/types/components';
import type { Group, Role, User, UserGroup } from '~/types/models';

import * as Api from '~/api';

const userRolesColumn: CollectionColumnDefinition<User, Role> = {
  title: 'Rôles globaux',
  editable: true,
  key: 'roles',
  mapOptions(items: Role[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  prefetchOptions: prefetchOptions<Role>('roles', [
    {
      id: '¤',
      humanId: '',
      sequence: 0,
      name: 'Aucun rôle global',
      description: '',
      slug: 'none',
      created: '',
      lastUpdated: '',
      usersCount: 0,
    },
  ]),
  render: (value) => {
    if (!Array.isArray(value)) {
      return <em>Chargement...</em>;
    }

    if (value.length === 0) {
      return <em>Aucun rôle global</em>;
    }

    return (
      <ul style={{ margin: 0 }}>
        {value.map((r) => (
          <li key={r.id}>{r.name}</li>
        ))}
      </ul>
    );
  },
  sortable: false,
  toLabel: toLabel('roles', 'Rôle', 'Aucun rôle global'),
  type: 'collection',
};

const userGroupsColumn: CollectionColumnDefinition<User, Group> = {
  title: 'Groupes',
  editable: true,
  key: 'groups',
  mapOptions(items: Group[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  prefetchOptions: prefetchOptions<Group>('groups', [
    {
      id: '¤',
      name: 'Aucun groupe',
      callsCount: 0,
      canLeaveMessage: false,
      contactsCount: 0,
      created: '',
      description: '',
      humanId: '',
      lastUpdated: '',
      meta: {},
      script: '',
      sequence: 0,
    },
  ]),
  render: (value) => {
    if (!Array.isArray(value)) {
      return <em>Chargement...</em>;
    }

    if (value.length === 0) {
      return <em>Aucun groupe</em>;
    }

    return (
      <ul style={{ margin: 0 }}>
        {value.map((g) => (
          <li key={g.id}>{g.name}</li>
        ))}
      </ul>
    );
  },
  sortable: false,
  toLabel: toLabel('groups', 'Groupe', 'Aucun groupe'),
  type: 'collection',
};

const userUserGroupsColumn: CollectionColumnDefinition<User, UserGroup> = {
  title: 'Permissions',
  editable: true,
  importable: true,
  key: 'userGroups',
  mapOptions(items: any[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  async prefetchOptions() {
    try {
      const response = await Api.cache('groups', async () => await Api.index<UserGroup>('/groups', { pageSize: 1000, sortBy: 'name' }));
      const noGroup: unknown = {
        id: '¤',
        humanId: '',
        sequence: 0,
        name: 'Aucune permission',
        created: '',
        lastUpdated: '',
        callsCount: 0,
        contactsCount: 0,
      };
      const groups: unknown = response.items;
      return [noGroup as UserGroup, ...(groups as UserGroup[])];
    } catch (e) {
      Api.handleError(e);
    }

    return [];
  },
  render: (value) => {
    if (!Array.isArray(value)) {
      return <em>Chargement...</em>;
    }

    if (value.length === 0) {
      return <em>Aucune permission</em>;
    }

    return (
      <ul className='CollectionColumn' style={{ margin: 0 }}>
        {value.map((u) => (
          <li key={u.id}>
            {u.group?.name}
            <ul>{u.roles?.map((r) => <li key={r.id}>{r.name}</li>)}</ul>
          </li>
        ))}
      </ul>
    );
  },
  sortable: false,
  toLabel: toLabel('groups', 'Permission', 'Aucune permission'),
  type: 'collection',
  with: 'userGroups,userGroups.roles,userGroups.group',
};

export const Columns: Array<ColumnDefinition<User>> = [
  {
    editable: true,
    key: 'name',
    sortable: true,
    title: 'Nom',
    type: 'string',
  },
  {
    editable: true,
    key: 'email',
    sortable: true,
    title: 'Courriel',
    type: 'string',
  },
  {
    editable: false,
    filterable: false,
    importable: false,
    key: 'meta',
    sortable: false,
    title: "Rubriques d'aide consultées",
    type: 'string',
    render: (value) => {
      if (!value || !Array.isArray(value.documentationPagesSeen) || value.length === 0) {
        return <em>Aucune consultation consignée</em>;
      }

      const pages: string[] = value.documentationPagesSeen ?? [];

      return (
        <ul style={{ margin: 0 }}>
          {pages.map((d, index) => (
            <li key={index}>{d}</li>
          ))}
        </ul>
      );
    },
  },
  {
    importable: false,
    key: 'lastLogin',
    presets: DateFilterPresets.Past,
    title: 'Dernière connexion',
    type: 'datetime',
    sortable: true,
    render: (v) => (v ? <DateTime value={v} /> : <span>Jamais connecté</span>),
  },
  userRolesColumn,
  userGroupsColumn,
  userUserGroupsColumn,
  ...ModelColumns,
];
